import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import PanelContent from './PanelContent';
import { usePanelGroupContext } from './PanelGroup';
import panelGroupStyles from './panelGroup.module.scss';

interface IAdditionalProps {
  prefixCls?: string,
  onItemClick?: () => {},
  isActive?: boolean,
}

interface IProps {
  className?: string,
  id: string,
  dataTest?: string,
  children?: React.ReactNode,
  destroyOnToggle?: boolean,
  header?: string | number | React.ReactNode,
  headerClass?: string,
  activeClass?: string,
  arrowWrapperClass?: string,
  contentClass?: string,
  showArrow?: boolean,
  arrow?: React.ReactNode | null,
  arrowActive?: React.ReactNode | null,
  style?: object,
  disabled?: boolean,
  isActive?: boolean,
  renderDefaultArrow?: boolean,
}

const Panel = (props: IProps) => {
  const {
    className,
    id,
    dataTest = '',
    style,
    destroyOnToggle = false,
    header,
    headerClass,
    activeClass = '',
    arrowWrapperClass = '',
    contentClass,
    children,
    showArrow = true,
    arrow = null,
    arrowActive = null,
    disabled,
    renderDefaultArrow,
  } = props;
  const { activeKey, getItemProps, destroyPanel } = usePanelGroupContext();

  const [additionalPanelProps, setAdditionalPanelProps] = useState<IAdditionalProps>(props);
  const [isActive, setIsActive] = useState(props.isActive);
  const { prefixCls, onItemClick } = additionalPanelProps;

  useEffect(() => {
    setAdditionalPanelProps(getItemProps(props));
    setIsActive(getItemProps(props).isActive);
    return () => destroyPanel(id);
  }, [activeKey]);

  const headerCls = cn(panelGroupStyles.panelGroupHeader, {
    [`${ prefixCls }Header`]: true,
    [panelGroupStyles.panelGroupHeaderActive]: isActive,
    [`${ prefixCls }HeaderActive`]: isActive,
    [`${ prefixCls }HeaderDisabled`]: disabled,
  }, headerClass);

  const itemCls = cn(
    panelGroupStyles.panelGroupItem,
    {
      [`${ prefixCls }Item`]: true,
      [`${ prefixCls }ItemActive`]: isActive,
      [`${ prefixCls }ItemDisabled`]: disabled,
      [activeClass]: isActive,
    },
    className,
  );

  const headerArrowCls = cn(
    panelGroupStyles.panelGroupHeaderArrow,
    {
      [`${ prefixCls }HeaderArrow`]: true,
      [`${ prefixCls }HeaderArrowActive`]: isActive,
      [`${ prefixCls }HeaderArrowDisabled`]: disabled,
    },
    arrowWrapperClass,
  );

  const headerId = `${ id }Header`;
  const contentId = `${ id }Content`;

  // Clicking anywhere in the header toggles the panel, so the whole title line is clickable
  // and not just the empty background. The one exception is a click landing on a nested
  // interactive control of its own (e.g. a "select all" checkbox or a copy-to-clipboard
  // button in a custom header) — that control should keep its own click behavior instead
  // of also toggling the panel. `label` is included because clicking anywhere inside a
  // checkbox/radio's <label> (including its decorative, non-input children) re-dispatches
  // a click targeted at that label's descendant, not necessarily the <input> itself.
  const handleHeaderClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    const hitsOwnInteractiveControl = target !== event.currentTarget &&
      !(target instanceof HTMLAnchorElement) &&
      target.closest('button, input, select, textarea, label, [role="button"]');

    if (!hitsOwnInteractiveControl) {
      onItemClick();
    }
  };

  return (
    <div
      id={ id }
      className={ itemCls }
      style={ style }
    >
      <div
        id={ headerId }
        className={ headerCls }
        data-test={ `${ dataTest || '' }Panel` }
        onClick={ handleHeaderClick }
        role="tab"
        aria-expanded={ isActive }
      >
        { header }
        { showArrow && (
          // Labelled by the header so the accessible name is the panel title; the recursion guard in
          // the name computation keeps the button's own content out of it. aria-expanded stays on the
          // header rather than moving here, so a panel still exposes exactly one expanded state.
          <button
            type="button"
            className={ headerArrowCls }
            onClick={ onItemClick }
            disabled={ disabled }
            aria-controls={ contentId }
            aria-labelledby={ headerId }
            data-test="panelGroupHeaderArrowButton"
          >
            { isActive && !renderDefaultArrow ? <>{ arrowActive }</> : <>{ arrow }</> }
          </button>
        ) }
      </div>
      <PanelContent
        id={ contentId }
        labelledBy={ headerId }
        destroyOnToggle={ destroyOnToggle }
        prefixCls={ prefixCls }
        isActive={ isActive }
        contentClass={ contentClass }
      >
        { children }
      </PanelContent>
    </div>
  );
};

export default Panel;
